import { Modal } from '@dixa/lib-shared-habla';
import { AsyncStatus } from 'lib-shared-legacy-types';
import { cloneElement, ReactElement, useEffect, useState } from 'react';

import { TwitterAccount, twitterApi, TwitterToken } from 'apis/twitter';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { TwitterConnect } from './components/twitterConnect/twitterConnect';
import { TwitterDetails } from './components/twitterDetails/twitterDetails';
import styles from './twitterModal.module.scss';

type TwitterModalProps = {
  account?: TwitterAccount;
  buttonComponent: ReactElement;
};

export const TwitterModal = ({
  account,
  buttonComponent,
}: TwitterModalProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const queues = useAppSelector((state) => state.settings.queues.queues);

  const [modalOpen, setModalOpen] = useState(false);
  const [twitterToken, setTwitterToken] = useState<TwitterToken>();
  const [twitterAccount, setTwitterAccount] = useState<TwitterAccount>();
  const [asyncStatus, setAsyncStatus] = useState<AsyncStatus>('succeeded');

  const defaultQueue = queues.find((queue) => queue.is_default);

  useEffect(() => {
    setTwitterAccount(account);
  }, [account]);

  const handleCloseModal = () => {
    setModalOpen(false);
    // Only clear account if were in the connect flow otherwise we're editing
    if (!account) {
      setTwitterAccount(undefined);
    }
  };

  const handleConnect = (account: TwitterAccount) => {
    setTwitterToken(undefined);
    setTwitterAccount(account);
  };

  async function handleAddTwitter() {
    if (twitterAccount) {
      setModalOpen(true);
    } else {
      try {
        setAsyncStatus('loading');

        const response = await dispatch(
          twitterApi.endpoints.readConnectToken.initiate(undefined, {
            forceRefetch: true,
          }),
        ).unwrap();

        window.open(
          response.data.authorize_url,
          '_blank',
          'toolbar=0,location=0,menubar=0,height=800,width=600',
        );

        setTwitterToken(response.data);
        setModalOpen(true);
        setAsyncStatus('succeeded');
      } catch (error) {
        notify(
          {
            title: 'An error occurred when trying to fetch the Twitter token.',
          },
          NotificationType.Error,
        );
        setAsyncStatus('failed');
      }
    }
  }

  // keep track of add vs edit to provide correct initial values and texts
  const view = account ? 'edit' : 'add';

  return (
    <>
      {cloneElement(buttonComponent, {
        onClick: handleAddTwitter,
        isLoading: asyncStatus === 'loading',
      })}

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        className={styles.modal}
      >
        {twitterAccount && (
          <TwitterDetails
            twitterData={twitterAccount}
            onCloseModal={handleCloseModal}
            view={view}
          />
        )}

        {twitterToken && defaultQueue && (
          <TwitterConnect
            twitterToken={twitterToken}
            onCloseModal={handleCloseModal}
            onConnect={handleConnect}
            defaultQueue={defaultQueue}
          />
        )}
      </Modal>
    </>
  );
};
